/* eslint-disable import/no-unresolved, import/no-extraneous-dependencies, no-nested-ternary */
import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  OmniverseHeader,
  LoaderError,
  Placeholder,
  ApplicationCard,
  ApplicationPages,
} from '../components';
import { useOmniverseConfig } from '../hooks/useConfig';
import { getStreamingApps, StreamingApp } from '../state/Apps';
import { comparePageOrder, getPages } from '../state/Pages';

/**
 * Displays applications available for streaming.
 * Hides applications that are currently inactive in NVCF.
 */
export default function OmniverseHome() {
  const config = useOmniverseConfig();

  const [searchParams] = useSearchParams();
  const {
    isLoading: isLoadingApps,
    data: appsByPages,
    error: appError,
  } = useQuery<Map<StreamingApp['page'], StreamingApp[]>>({
    queryKey: ['get-apps'],
    queryFn: async () => getStreamingApps({ config }),
  });

  const {
    isLoading: isLoadingPages,
    data: pages,
    error: pageError,
  } = useQuery({
    queryKey: ['get-pages'],
    queryFn: async () => getPages({ config }),
  });

  const pageNames: string[] = Array.from(appsByPages?.keys() ?? []).sort(
    (a, b) => {
      const pageA = pages?.get(a);
      const pageB = pages?.get(b);
      return comparePageOrder(pageA, pageB);
    }
  );

  const selectedPage: string | undefined =
    searchParams.get('page') ?? pageNames?.[0];

  const apps = selectedPage ? appsByPages?.get(selectedPage) : [];
  const categories =
    apps?.reduce((cats, app) => {
      const categoryName = app.category ?? '';
      const category = cats[categoryName] ?? [];
      category.push(app);
      cats[categoryName] = category;
      return cats;
    }, {} as Record<string, StreamingApp[]>) ?? {};

  const error = appError || pageError;

  return (
    <Box>
      <OmniverseHeader />
      <Box sx={{ px: 4, py: 3 }}>
        {isLoadingApps || isLoadingPages ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <LoaderError title={'Failed to load streaming applications'}>
            {error.toString()}
          </LoaderError>
        ) : appsByPages!.size ? (
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            {/* <ApplicationPages pages={pageNames} selectedPage={selectedPage} /> */}
            <Box sx={{ flex: 1 }}>
              {Object.entries(categories).map(
                ([category, categoryApps]: [string, StreamingApp[]]) => (
                  <Card
                    key={category}
                    sx={{
                      mb: 3,
                      boxShadow: '3px 3px 5px #E8E8E8',
                      border: '0.1rem solid #E8E8E8',
                    }}
                  >
                    <Box sx={{ p: 3 }}>
                      {category && (
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <GridViewOutlinedIcon
                              sx={{ color: 'primary.main' }}
                            />
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                              }}
                            >
                              {category}
                            </Typography>
                          </Box>
                          <Divider sx={{ mb: 3 }} />
                        </>
                      )}

                      <Grid container spacing={3}>
                        {categoryApps.map((app: StreamingApp) => (
                          <Grid item xs={12} sm={6} lg={4} key={app.id}>
                            <ApplicationCard app={app} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Card>
                )
              )}
            </Box>
          </Box>
        ) : (
          <Placeholder
            icon={
              <ViewModuleOutlinedIcon
                sx={{ fontSize: 100, color: 'currentColor' }}
              />
            }
          >
            No streaming applications are currently available.
          </Placeholder>
        )}
      </Box>
    </Box>
  );
}
